/* Keep the Duo's complimentary comb quantity in step with the cart. */
(function (scope) {
  'use strict';
  function createGiftSynchronizer({fetch: request, root = '/', duoVariantId, giftVariantId}) {
    let queue = Promise.resolve();
    const base = root.endsWith('/') ? root : root + '/';
    function count(cart) {
      const duo = cart.items.filter(item => String(item.variant_id) === String(duoVariantId))
        .reduce((sum, item) => sum + item.quantity, 0);
      const gifts = cart.items.filter(item => String(item.variant_id) === String(giftVariantId) &&
        (item.properties?._vh_glove_gift === 'duo' || item.properties?.['Gift for'] === 'VelaHush Glove Duo'));
      return {duo, gifts, giftQuantity: gifts.reduce((sum, item) => sum + item.quantity, 0)};
    }
    async function json(path, body) {
      const response = await request(base + path, {
        credentials: 'same-origin', cache: 'no-store',
        headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
        ...(body ? {method: 'POST', body: JSON.stringify(body)} : {})
      });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error('Could not update your complimentary gift. Please refresh your cart and try again.');
      return result;
    }
    async function reconcile() {
      let cart = await json('cart.js');
      let changed = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        const state = count(cart);
        if (state.duo === state.giftQuantity) return {cart, changed};
        changed = true;
        if (!state.gifts.length && state.duo > 0) {
          await json('cart/add.js', {items: [{id: giftVariantId, quantity: state.duo,
            properties: {'Gift for': 'VelaHush Glove Duo', '_vh_glove_gift': 'duo'}}]});
          cart = await json('cart.js');
        } else {
          // Use line keys: discount allocation can split a variant across several lines.
          const updates = Object.fromEntries(state.gifts.map((item, index) => [item.key, index === 0 ? state.duo : 0]));
          cart = await json('cart/update.js', {updates});
        }
      }
      const state = count(cart);
      if (state.duo !== state.giftQuantity) throw new Error('Could not match your complimentary gifts to your Duo sets. Please refresh your cart.');
      return {cart, changed};
    }
    return {
      count,
      sync() {
        const pending = queue.then(reconcile);
        queue = pending.catch(() => {});
        return pending;
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {createGiftSynchronizer};
  if (!scope.document) return;
  const configNode = scope.document.getElementById('vh-glove-gift-config');
  if (!configNode || scope.vhGloveSyncGifts) return;
  const config = JSON.parse(configNode.textContent);
  const synchronizer = createGiftSynchronizer({fetch: scope.fetch.bind(scope), ...config});
  let relevant = config.hasRelevantItems || config.isGlovePdp;
  scope.vhGloveSyncGifts = async function () {
    const result = await synchronizer.sync();
    const state = synchronizer.count(result.cart);
    relevant = state.duo > 0 || state.gifts.length > 0;
    const badge = scope.document.getElementById('cart-count');
    if (badge) badge.textContent = result.cart.item_count;
    return result.cart;
  };
  function reportError(error) {
    const target = scope.document.getElementById('cart-errors') || scope.document.getElementById('CartDrawer-CartErrors') || scope.document.getElementById('vh-cart-message');
    if (target) {target.textContent = error.message; target.hidden = false;}
  }
  const pause = ms => new Promise(resolve => scope.setTimeout(resolve, ms));
  async function waitForQuantityUpdate() {
    // Allow the theme's existing 300ms quantity debounce to start, then await it.
    await pause(350);
    const deadline = Date.now() + 15000;
    while ([...scope.document.querySelectorAll('cart-items,cart-drawer-items')].some(node => node.vhQuantityBusy)) {
      if (Date.now() > deadline) throw new Error('Your cart is still updating. Please try checkout again.');
      await pause(50);
    }
  }
  let checkingOut = false;
  let allowSubmit = false;
  scope.document.addEventListener('submit', async event => {
    if (allowSubmit || event.submitter?.name !== 'checkout' || !relevant) return;
    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    const formId = event.target.id;
    try {
      await waitForQuantityUpdate();
      const cart = await scope.vhGloveSyncGifts();
      if (!cart.item_count) return;
      // Section refreshes can replace the checkout button while quantities change.
      const form = scope.document.getElementById(formId) || event.target;
      const button = [...scope.document.querySelectorAll('button[name="checkout"],input[name="checkout"]')]
        .find(node => node.form === form && !node.disabled);
      if (!button || !form.isConnected) throw new Error('Please refresh your cart before checking out.');
      // Submit the reconciled server cart; stale updates[] inputs would restore old gift quantities.
      const quantityInputs = [...form.elements].filter(input => input.name === 'updates[]');
      const disabledStates = quantityInputs.map(input => input.disabled);
      quantityInputs.forEach(input => {input.disabled = true;});
      try {
        allowSubmit = true;
        form.requestSubmit(button);
      } finally {
        allowSubmit = false;
        quantityInputs.forEach((input, index) => {input.disabled = disabledStates[index];});
      }
    } catch (error) {
      reportError(error);
    } finally {
      checkingOut = false;
    }
  }, true);
  async function boot() {
    if (!config.hasRelevantItems && !config.isGlovePdp) return;
    try {
      const result = await synchronizer.sync();
      const state = synchronizer.count(result.cart);
      relevant = state.duo > 0 || state.gifts.length > 0;
      const badge = scope.document.getElementById('cart-count');
      if (badge) badge.textContent = result.cart.item_count;
      if (result.changed && config.isCartPage) scope.location.reload();
    } catch (error) {reportError(error);}
  }
  if (scope.document.readyState === 'loading') scope.document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
})(typeof window !== 'undefined' ? window : globalThis);
